import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Button } from '@raptormaps/button';
import { Text } from '@raptormaps/text';
import toastr from 'toastr';
import _ from 'lodash';

import { Switch } from '@raptormaps/switch';
import { Pill } from '@raptormaps/pill';
import FeatureApiClient from '../../apiClient/FeatureApiClient';
import styled from 'styled-components';
import theme from '@raptormaps/theme';
import {
  SOFTWARE_TEAM_FLAGS,
  VENDOR_FLAGS,
} from '../../constants/featureFlagGroups';

const GroupSection = styled.div`
  margin-bottom: 24px;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid ${theme.colors.slate_100};
  gap: 12px;
`;

const FeatureInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

/**
 * Modal that is rendered when a user selects an org from the table view of org permissions.
 * Features are grouped into Product, Software Team, and Vendor sections.
 */
const PermissionModal = ({
  show,
  handleClose,
  selectedOrg,
  orgFeatures,
  setOrgFeatures,
  allFeatures,
  farmLevel = false,
}) => {
  const [featureDiff, setFeatureDiff] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const enabledFeatures = new Set(orgFeatures.map(o => o.feature_id));
    setFeatureDiff(allFeatures.map(f => enabledFeatures.has(f.id)));
  }, [orgFeatures]);

  const handleToggle = index => {
    setFeatureDiff(prev =>
      prev.map((status, idx) => (idx === index ? !status : status)),
    );
  };

  const handleSave = async () => {
    setLoading(true);

    const zippedDiff = _.zip(allFeatures, featureDiff);
    const enabledFeatures = new Set(orgFeatures.map(o => o.feature_id));

    const featuresToDisable = zippedDiff.filter(
      ([feature, toggledStatus]) =>
        enabledFeatures.has(feature.id) && !toggledStatus,
    );
    const featuresToEnable = zippedDiff.filter(
      ([feature, toggledStatus]) =>
        !enabledFeatures.has(feature.id) && toggledStatus,
    );

    const featureApiClient = new FeatureApiClient();
    const key = farmLevel === true ? 'farmLevel' : 'orgLevel';
    const featureApi = {
      farmLevel: {
        enableFeature: featureApiClient.enableFarmFeature,
        disableFeature: featureApiClient.disableFarmFeature,
        params: { solar_farm_id: selectedOrg.id },
      },
      orgLevel: {
        enableFeature: featureApiClient.enableOrgFeature,
        disableFeature: featureApiClient.disableOrgFeature,
        params: { org_id: selectedOrg.id },
      },
    };

    try {
      if (!_.isEmpty(featuresToDisable)) {
        await Promise.all(
          featuresToDisable.map(([feature]) =>
            featureApi[key].disableFeature(selectedOrg.id, feature.id),
          ),
        );
      }
      if (!_.isEmpty(featuresToEnable)) {
        await Promise.all(
          featuresToEnable.map(([feature]) =>
            featureApi[key].enableFeature(selectedOrg.id, feature.id),
          ),
        );
      }
      toastr.success('Permissions Saved', 'Success', {
        timeOut: 3000,
        closeButton: true,
      });
    } catch (error) {
      console.error(error);
      toastr.error(error);
    }

    featureApiClient
      .getOrgFeatures(featureApi[key].params)
      .then(res => setOrgFeatures(res));

    setLoading(false);
  };

  const softwareTeamFeatures = allFeatures
    .map((f, idx) => ({ feature: f, idx }))
    .filter(({ feature }) => SOFTWARE_TEAM_FLAGS.has(feature.internal_keycode));

  const vendorFeatures = allFeatures
    .map((f, idx) => ({ feature: f, idx }))
    .filter(({ feature }) => VENDOR_FLAGS.has(feature.internal_keycode));

  const productFeatures = allFeatures
    .map((f, idx) => ({ feature: f, idx }))
    .filter(
      ({ feature }) =>
        !SOFTWARE_TEAM_FLAGS.has(feature.internal_keycode) &&
        !VENDOR_FLAGS.has(feature.internal_keycode),
    );

  const renderFeatureRow = ({ feature, idx }) => (
    <FeatureRow key={feature.id}>
      <FeatureInfo>
        <Text variant="paragraph_small_bold">{feature.name}</Text>
        {feature.description && (
          <Text variant="paragraph_small" color="neutral_600">
            {feature.description}
          </Text>
        )}
      </FeatureInfo>
      <Switch
        checked={featureDiff[idx]}
        onCheckedChange={() => handleToggle(idx)}
        disabled={loading}
        size="xlarge"
      />
    </FeatureRow>
  );

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <div className="permission-modal-title">
          {farmLevel && (
            <Modal.Title>
              Org: {selectedOrg.org_id} - {selectedOrg.org_name}
            </Modal.Title>
          )}
          <Modal.Title>
            # {selectedOrg.id} - {selectedOrg.name}
          </Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {show && (
          <>
            {vendorFeatures.length > 0 ? (
              <GroupSection>
                <Pill variant="orange" size="small">
                  Vendor
                </Pill>
                {vendorFeatures.map(renderFeatureRow)}
              </GroupSection>
            ) : null}
            {productFeatures.length > 0 ? (
              <GroupSection>
                <Pill variant="neutral" size="small">
                  Product
                </Pill>
                {productFeatures.map(renderFeatureRow)}
              </GroupSection>
            ) : null}
            {softwareTeamFeatures.length > 0 ? (
              <GroupSection>
                <Pill variant="violet" size="small">
                  Software Team
                </Pill>
                {softwareTeamFeatures.map(renderFeatureRow)}
              </GroupSection>
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{' '}
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PermissionModal;
